import React, { useState, type FC } from 'react';
import { FinalDecisionMakingProperty } from '../../utility/types';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import '../../../../app/assets/stylesheets/fdm-property-list-styles.css';
import 'primereact/resources/themes/lara-light-indigo/theme.css';   // theme
import 'primeflex/primeflex.css';                                   // css utility
import 'primeicons/primeicons.css';
import 'primereact/resources/primereact.css';
import ConfirmDeleteLSPJModal from './ConfirmDeleteLSPJModal';
import { TrashIcon } from '@heroicons/react/20/solid';
import { ListSortPropertyJoins } from '../../utility/types';
import { DecisionFormDataProps } from '../../hooks/useDecisionMakingSubmission';
import { formatOfferPrice, offerPdfUrl, StoredOffer } from '../../utility/contractOffers';
import ListingEmailCell from './ListingEmailCell';
import { ListingEmailStatus } from '../../utility/listingEmail';

type FinalDecisionMakingTableProps = {
  properties: FinalDecisionMakingProperty[];
  activeListSortId: string;
  listSortPropertyJoins: ListSortPropertyJoins[];
  onDisplayedRowsChange?: (rows: FinalDecisionMakingProperty[]) => void;
  // Contract mode: a checkbox beside each address
  selectionMode?: boolean;
  selectedIds?: string[];
  onToggleSelect?: (propertyId: string) => void;
  onToggleAll?: (checked: boolean) => void;
  // Stored contracts by property (newest first), shown in the Contracts column
  offersByProperty?: Record<string, StoredOffer[]>;
  // The Contracts column can be hidden; it is never part of the printed page
  showContracts?: boolean;
  // Email Listing Agent mode: a draft-email button (or what is missing) beside each address
  emailMode?: boolean;
  emailStatuses?: Record<string, ListingEmailStatus>;
  emailClient?: string;
}

const FinalDecisionMakingTable: FC<FinalDecisionMakingTableProps> = ({
  properties, activeListSortId, listSortPropertyJoins, onDisplayedRowsChange,
  selectionMode = false, selectedIds = [], onToggleSelect, onToggleAll, offersByProperty = {}, showContracts = true,
  emailMode = false, emailStatuses = {}, emailClient,
}) => {
  const hasOffers = Object.keys(offersByProperty).length > 0;
  const allSelected = properties?.length > 0 && properties.every((p) => selectedIds.includes(String(p.propertyId)));
  const [ modalVisible, setModalVisible ] = useState(false);
  const [ selectedPropertyId, setSelectedPropertyId ] = useState(null);

  const handleDeleteClick = (propertyId: string) => {
    setSelectedPropertyId(propertyId);
    setModalVisible(true);
  };

  const handleDeleteConfirm = () => {
    properties.splice(properties.findIndex((property) => property.propertyId === selectedPropertyId), 1);
  };

  return (
    <>
      <div id='fdm-properties-list-container' className='h-full flex flex-col'>
        {properties?.length > 0 ? (
          <div id="fdm-properties-list" className="flex-1 min-h-0">
            <DataTable
              id="fdm-properties-data-table"
              value={properties}
              onValueChange={(rows) => onDisplayedRowsChange?.(rows as FinalDecisionMakingProperty[])}
              sortMode="multiple"
              tableStyle={{ minWidth: '50rem' }}
              size="normal"
              removableSort
              scrollable
              pt={{
                paginator: { pageButton: { className: 'rounded-full' } },
                header: { className: 'fdm-table-header ' },
                headerRow: { className: 'fdm-table-header-row ' },
                table: { className: 'fdm-table-table ' },
                footer: { className: 'fdm-table-footer ' },
                wrapper: { className: 'fdm-table-wrapper' },
                row: {
                  root: {
                    className: 'fdm-table-row ',
                  }
                }
              }}
            >
              {hasOffers && showContracts && (
                <Column
                  key="contracts"
                  header="Contracts"
                  style={{ minWidth: 200 }}
                  headerClassName='fdm-contracts-col'
                  bodyClassName='fdm-contracts-col'
                  body={(rowData) => {
                    const offers = offersByProperty[String(rowData.propertyId)] ?? [];
                    if (offers.length === 0) return <span className='text-gray-400'>-</span>;
                    return (
                      <ul data-tag='contracts-cell'>
                        {offers.map((offer, index) => (
                          <li key={offer.id}>
                            <a
                              className='text-indigo-600 underline'
                              href={offerPdfUrl(offer.id)}
                              target='_blank'
                              rel='noreferrer'
                              title={`Made by ${offer.created_by_name} for ${offer.buyer_name}`}
                            >
                              {index === 0 ? 'Latest' : 'Earlier'}: {formatOfferPrice(offer.price)}, {new Date(offer.created_at).toLocaleDateString()}
                            </a>
                          </li>
                        ))}
                      </ul>
                    );
                  }}
                />
              )}
              {selectionMode && (
                <Column
                  key="contractSelect"
                  header={
                    <input
                      type='checkbox'
                      aria-label='Select all properties'
                      checked={allSelected}
                      onChange={(e) => onToggleAll?.(e.target.checked)}
                    />
                  }
                  bodyStyle={{ textAlign: 'center' }}
                  body={(rowData) => (
                    <input
                      type='checkbox'
                      data-tag='contract-select-checkbox'
                      aria-label={`Select ${rowData.address}`}
                      checked={selectedIds.includes(String(rowData.propertyId))}
                      onChange={() => onToggleSelect?.(String(rowData.propertyId))}
                    />
                  )}
                />
              )}
              <Column
                field="propertyDetails"
                key="propertyDetails"
                header={'Edit Decision'}
                bodyStyle={{ textAlign: 'center' }}
                body={(rowData) => {
                  // Or do we need to set the session storage propertyId here ?
                  return (
                    <a href={`/decision-making/${activeListSortId}?propertyId=${rowData.propertyId}`} className="text-sm font-semibold ml-2 text-gray-900" >
                      <span aria-hidden="true">←</span>
                    </a>);
                }}
              />
              <Column
                field="propertyDetailsDelete"
                key="propertyDetailsDelete"
                header={'Delete Decision'}
                bodyStyle={{ textAlign: 'center' }}
                body={(rowData) => {
                  return (
                    <button
                      data-tag='delete-property-button'
                      id={`delete-property-button-${rowData.propertyId}`}
                      className="text-sm font-semibold ml-2 text-gray-900 hover:text-red-500"
                      onClick={() => {
                        handleDeleteClick(rowData.propertyId);
                      }}
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>);
                }}
              />
              <Column field="customListSortNumber" header="Auction ID#" sortable style={{ width: '5%', textAlign: 'center' }}></Column>

              <Column field="address" header="Address" sortable style={{ minWidth: 300 }}
                body={(data) => (
                  <>
                    <div
                      className="hover:cursor-default hover:underline"
                      onClick={() => {
                        window.open(`/properties/${data.propertyId}`, '_blank');
                      }}
                    >{data.address}</div>
                    {emailMode && <ListingEmailCell listSortId={activeListSortId} propertyId={String(data.propertyId)} status={emailStatuses[String(data.propertyId)]} emailClient={emailClient} />}
                  </>
                )}
              />

              <Column field="arvSelected" header="Estimated Value" sortable style={{ minWidth: '25%' }}></Column>
              <Column field="approachName" header="Approach Name" sortable style={{ minWidth: '75px' }}></Column>
              <Column field="maxBid" header="Max Bid" sortable style={{ minWidth: '25%' }}></Column>
              <Column field="repairEstimate" header="Repair Estimate" sortable style={{ minWidth: '25%' }}></Column>
              <Column field="profitOnMaxBid" header="Profit on Max Bid" sortable style={{ minWidth: '100px' }}></Column>
              <Column field="roiPercent" header="ROI Percent" sortable style={{ minWidth: '25%' }}></Column>
              <Column field="sqFt" header="SqFt" sortable style={{ minWidth: '25%' }}></Column>
              <Column field="yearBuilt" header="Year Built" sortable style={{ minWidth: '25%' }}></Column>
              <Column field="bedrooms" header="Bed" sortable style={{ minWidth: '25%' }}></Column>
              <Column field="bathrooms" header="Bath" sortable style={{ minWidth: '25%' }}></Column>
              <Column field="acreage" header="Acreage" sortable style={{ minWidth: '25%' }}></Column>
            </DataTable>
          </div>
        ) : (
          <div className='flex justify-center items-center h-full m-5 text-lg text-gray-500'>
          No properties in this list are ready for Final Decision
          </div>
        )}
      </div>
      {modalVisible && (
        <ConfirmDeleteLSPJModal
          propertyId={selectedPropertyId}
          onHide={() => setModalVisible(false)}
          activeListSortId={activeListSortId}
          handleDeleteConfirm={handleDeleteConfirm}
          listSortPropertyJoin={listSortPropertyJoins.find((join) => join.property_id === selectedPropertyId) as DecisionFormDataProps}
        />
      )}
    </>
  );
};

export default FinalDecisionMakingTable;
